import { ReactNode, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  User, 
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { AnimatedBackground } from './AnimatedBackground';
import { 
  DashboardIcon, 
  AIAssistantIcon, 
  SessionsIcon, 
  SettingsIcon,
  AnchorUpIcon 
} from '@/components/icons/CustomIcons';

// Custom DryVox Logo - Anchor design
const DryVoxLogo = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-7 h-7'
  };
  
  return (
    <svg viewBox="0 0 32 32" fill="none" className={sizeClasses[size]}>
      <circle cx="16" cy="6" r="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <path d="M16 10V26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M10 16H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M8 28C8 22 12 20 16 26C20 20 24 22 24 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M4 14C5.5 12 5.5 10 4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      <path d="M28 14C26.5 12 26.5 10 28 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    </svg>
  );
};

interface AppShellProps {
  children: ReactNode;
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { icon: DashboardIcon, label: 'Dashboard', href: '/dashboard' },
  { icon: AIAssistantIcon, label: 'AI Assistant', href: '/assistant' },
  { icon: SessionsIcon, label: 'Sessions', href: '/sessions' },
  { icon: SettingsIcon, label: 'Settings', href: '/settings' },
];

const SIDEBAR_COLLAPSED_KEY = 'dryvox_sidebar_collapsed';

/**
 * AppShell - Main layout wrapper for authenticated pages
 * 
 * Features:
 * - Animated background
 * - Collapsible sidebar navigation with icon-only mode
 * - Smooth animations
 * - User profile section
 * - Mobile responsive
 * - Remembers collapsed state
 */
export function AppShell({ children }: AppShellProps) {
  const { user, logout } = useAuthStore();
  const { theme, resolvedTheme, setTheme } = useThemeStore();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  // Save collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const handleLogout = () => {
    logout();
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const sidebarWidth = isCollapsed ? 80 : 280;

  return (
    <div className="min-h-screen relative">
      {/* Animated Background */}
      <AnimatedBackground />

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 p-3 rounded-xl glass border border-white/10 lg:hidden"
      >
        {isMobileOpen ? (
          <X className="w-5 h-5 text-gray-300" />
        ) : (
          <Menu className="w-5 h-5 text-gray-300" />
        )}
      </button>

      {/* Mobile Backdrop */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileOpen(false)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ 
          width: sidebarWidth,
          x: isMobileOpen || typeof window !== 'undefined' && window.innerWidth >= 1024 ? 0 : -sidebarWidth 
        }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className={cn(
          'fixed left-0 top-0 h-full z-50',
          'glass border-r border-white/10',
          'flex flex-col',
          'lg:translate-x-0'
        )}
        style={{ width: sidebarWidth }}
      >
        {/* Logo */}
        <div className={cn(
          "border-b border-white/10 transition-all duration-300",
          isCollapsed ? "p-4" : "p-6"
        )}>
          <Link to="/dashboard" className="flex items-center gap-3">
            <motion.div 
              layout
              className={cn(
                "rounded-xl bg-gradient-to-br from-ocean-cyan to-ocean-blue flex items-center justify-center shadow-glow-cyan text-white",
                isCollapsed ? "w-12 h-12" : "w-10 h-10"
              )}
            >
              <DryVoxLogo size={isCollapsed ? "lg" : "md"} />
            </motion.div>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <h1 className="text-xl font-bold font-display text-gradient whitespace-nowrap">DryVox</h1>
                  <p className="text-xs text-gray-500 whitespace-nowrap">Voice AI Assistant</p>
                </motion.div>
              )}
            </AnimatePresence>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMobileOpen(false)}
                className={cn(
                  'relative flex items-center gap-3 rounded-xl transition-all duration-200',
                  isCollapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3',
                  isActive
                    ? 'bg-ocean-cyan/20 text-ocean-cyan border border-ocean-cyan/30'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                )}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-ocean-cyan')} />
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="font-medium whitespace-nowrap overflow-hidden"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {isActive && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute left-0 w-1 h-6 bg-ocean-cyan rounded-r-full"
                  />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse Toggle Button - Desktop only */}
        <div className="hidden lg:block p-3 border-t border-white/5">
          <button
            onClick={toggleCollapse}
            className={cn(
              "w-full flex items-center gap-3 rounded-xl transition-all duration-200",
              "text-gray-400 hover:text-white hover:bg-white/5",
              isCollapsed ? "px-3 py-3 justify-center" : "px-4 py-3"
            )}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>

        {/* User Section */}
        <div className={cn(
          "border-t border-white/10 transition-all duration-300",
          isCollapsed ? "p-3" : "p-4"
        )}>
          <div className={cn(
            "flex items-center rounded-xl bg-white/5",
            isCollapsed ? "p-2 justify-center" : "gap-3 px-4 py-3"
          )}>
            <div className={cn(
              "rounded-full bg-gradient-to-br from-ocean-purple to-ocean-pink flex items-center justify-center flex-shrink-0",
              isCollapsed ? "w-10 h-10" : "w-10 h-10"
            )}>
              <User className="w-5 h-5 text-white" />
            </div>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-w-0 overflow-hidden"
                >
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name || user?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{user?.tier || 'Pro'} Plan</p>
                </motion.div>
              )}
            </AnimatePresence>
            {!isCollapsed && (
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors group flex-shrink-0"
                title="Logout"
              >
                <AnchorUpIcon className="w-4 h-4 text-gray-400 group-hover:text-signal-error" />
              </button>
            )}
          </div>
          {isCollapsed && (
            <button
              onClick={handleLogout}
              className="w-full mt-2 p-2 rounded-lg hover:bg-white/10 transition-colors group flex items-center justify-center"
              title="Logout"
            >
              <AnchorUpIcon className="w-4 h-4 text-gray-400 group-hover:text-signal-error" />
            </button>
          )}
        </div>
      </motion.aside>

      {/* Main Content */}
      <motion.main 
        initial={false}
        animate={{ marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024 ? sidebarWidth : 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="min-h-screen"
      >
        {/* Theme Toggle - Top Right */}
        <div className="fixed top-4 right-4 z-40">
          <button
            onClick={toggleTheme}
            className={cn(
              'p-3 rounded-xl transition-all duration-300',
              'glass border',
              resolvedTheme === 'dark' 
                ? 'border-white/10 hover:border-ocean-cyan/30' 
                : 'border-gray-200 hover:border-ocean-cyan/50 shadow-md'
            )}
            title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {resolvedTheme === 'dark' ? (
              <Sun className="w-5 h-5 text-yellow-400" />
            ) : (
              <Moon className="w-5 h-5 text-slate-700" />
            )}
          </button>
        </div>

        <div className="p-4 lg:p-8">
          {children}
        </div>
      </motion.main>
    </div>
  );
}

export default AppShell;
